import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { Clock, ArrowRight, Lock } from "lucide-react";

export default function BlogList() {
  useSEO({
    title: "The Method — Boxing Journal | Better Through Boxing",
    description: "Boxing drills, footwork breakdowns, defensive technique, and coaching insights from Coach Chris Persaud. Practical content for athletes training online over Zoom, at home, or in the gym.",
    path: "/blog",
    keywords: ["online boxing coach", "boxing drills at home", "boxing footwork training online", "virtual boxing lessons", "boxing tips for beginners", "shadow boxing drills", "boxing journal blog"],
  });

  const [posts, setPosts] = useState([]);
  // Start in `idle` state so we render rich content immediately for SEO crawlers.
  // Only flip to `empty` after a real failed/zero fetch — never on initial render.
  const [status, setStatus] = useState("loading"); // loading | ready | empty

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/blog/posts");
        if (cancelled) return;
        setPosts(data || []);
        setStatus((data || []).length > 0 ? "ready" : "empty");
      } catch {
        if (!cancelled) { setPosts([]); setStatus("empty"); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="App relative">
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="blog-list-page">
        {/* Hero — content-rich so SEO crawlers always see meaningful text */}
        <header className="mb-14 max-w-3xl">
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">The Method · Journal</div>
          <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-none mb-6">Boxing,<br />broken down.</h1>
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-4">
            Practical drills, technique breakdowns, and coaching notes from Coach Chris Persaud — the same coach behind every <Link to="/services" className="text-[#FF331F] hover:underline">live virtual Zoom boxing lesson</Link> at Better Through Boxing.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            Built for athletes who want to train better — at home, on the road, in the gym, or on Zoom. Topics cover boxing footwork training online, shadow boxing drills, defensive fundamentals, conditioning, and how to get the most out of online boxing coaching as a beginner.
          </p>
        </header>

        {/* About this journal — additional indexable copy */}
        <section className="border border-[#27272A] bg-[#0F0F0F] p-6 md:p-8 mb-12 grid md:grid-cols-3 gap-6" data-testid="blog-about-band">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">For Beginners</div>
            <p className="text-sm text-zinc-400 leading-relaxed">If you&apos;re new to boxing, the journal breaks down stance, the Core 4 punches, and how to start training at home without equipment.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">For Returning Athletes</div>
            <p className="text-sm text-zinc-400 leading-relaxed">Tune-up drills, footwork patterns, and conditioning frameworks for athletes shaking the rust off after time away.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Updated Regularly</div>
            <p className="text-sm text-zinc-400 leading-relaxed">New posts published as Coach Chris finds the questions worth answering. Want a topic covered? <Link to="/contact" className="text-[#FF331F] hover:underline">Suggest one</Link>.</p>
          </div>
        </section>

        {/* Posts grid OR skeleton OR empty-state-with-substance */}
        {status === "loading" && (
          <div className="grid md:grid-cols-2 gap-8" data-testid="blog-list-loading">
            {[0, 1].map((i) => (
              <div key={i} className="border border-[#27272A] bg-[#0F0F0F] overflow-hidden animate-pulse">
                <div className="aspect-[16/9] bg-[#1A1A1A]" />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-24 bg-[#1A1A1A]" />
                  <div className="h-6 w-3/4 bg-[#1A1A1A]" />
                  <div className="h-4 w-full bg-[#1A1A1A]" />
                </div>
              </div>
            ))}
          </div>
        )}

        {status === "ready" && (
          <div className="grid md:grid-cols-2 gap-8">
            {posts.map((post) => {
              const locked = post.is_coming_soon || post.status === "coming_soon";
              if (locked) {
                return (
                  <div
                    key={post.slug}
                    className="group border border-amber-700/40 bg-[#0F0F0F] overflow-hidden relative opacity-90"
                    data-testid={`blog-card-locked-${post.slug}`}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img src={post.cover} alt={post.title} className="w-full h-full object-cover grayscale" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/95 via-[#0F0F0F]/60 to-transparent" />
                      <div className="absolute top-3 left-3 bg-amber-500 text-black text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Coming Soon
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Lock className="h-10 w-10 text-amber-400/70" />
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">
                        <span>{post.category}</span>
                        <span>·</span>
                        <span>{post.level}</span>
                      </div>
                      <h2 className="font-display uppercase text-2xl md:text-3xl leading-tight mb-3 text-zinc-200">{post.title}</h2>
                      <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                      <div className="mt-4 inline-flex items-center text-amber-400 text-xs uppercase tracking-widest font-bold">
                        Unlocking soon
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group border border-[#27272A] bg-[#0F0F0F] overflow-hidden hover:border-[#FF331F]/50 transition-colors"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={post.cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/80 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 bg-[#FF331F] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">{post.category}</div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.read_time}</span>
                      <span>·</span>
                      <span>{post.level}</span>
                    </div>
                    <h2 className="font-display uppercase text-2xl md:text-3xl leading-tight mb-3 group-hover:text-[#FF331F] transition-colors">{post.title}</h2>
                    <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                    <div className="mt-4 inline-flex items-center text-[#FF331F] text-xs uppercase tracking-widest font-bold">
                      Read post <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {status === "empty" && (
          <section className="border border-[#27272A] bg-[#0F0F0F] p-10 md:p-14" data-testid="blog-list-empty">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Coming Soon</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-4">New posts in the works.</h2>
            <p className="text-zinc-300 leading-relaxed mb-3">Coach Chris is writing breakdowns of the drills, footwork patterns, and defensive techniques covered in every Better Through Boxing session. Subscribe via Instagram for the first heads-up when new posts drop.</p>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">In the meantime — the best content lives <em>inside</em> the sessions. Live virtual boxing lessons cover everything the blog will (and more), with real-time coaching you can&apos;t get from any article.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/services" className="inline-flex items-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm">See services <ArrowRight className="ml-2 h-3 w-3" /></Link>
              <a href="https://www.instagram.com/flow_state_method/" target="_blank" rel="noreferrer" className="inline-flex items-center border border-zinc-700 text-white hover:bg-zinc-900 font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm">Follow on Instagram</a>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
